<template>
  <div class="backstage-inline">
    <h4 class="inline-title">{{ title }}</h4>
    <div v-if="showMinimumHint" class="minimum-hint">
      Для отображения на сайте загрузите минимум 4 фото backstage (сейчас: {{ displayImages.length }}/4).
    </div>
    <div v-if="loading" class="state-block">Загрузка...</div>
    <div v-else-if="error" class="state-block error">{{ error }}</div>
    <div v-else-if="displayImages.length === 0" class="state-block">Нет изображений</div>
    <div v-else class="images-grid">
      <div
        v-for="image in displayImages"
        :key="image.id"
        class="image-card"
        :class="{ 'is-draggable': allowReorder }"
        :draggable="allowReorder"
        @dragstart="allowReorder ? handleDragStart($event, image) : null"
        @dragover.prevent="allowReorder ? handleDragOver($event, image) : null"
        @drop.prevent="allowReorder ? handleDrop($event, image) : null"
        @dragend="allowReorder ? handleDragEnd() : null"
      >
        <div class="image-preview">
          <img v-if="image.url" :src="image.url" :alt="'Backstage image ' + image.id" />
          <div v-else class="image-placeholder">Изображение</div>
        </div>
        <div v-if="showInfo" class="image-info">
          <div class="image-meta-row">
            <span>ID</span>
            <code>{{ image.id }}</code>
          </div>
          <div class="image-meta-row">
            <span>Файл ID</span>
            <code>{{ image.file_id }}</code>
          </div>
        </div>
        <div v-if="showActions" class="image-actions">
          <button @click="removeImage(image)" class="button button-danger !h-8 !px-3 !py-0 text-xs">Удалить</button>
        </div>
      </div>
    </div>

    <div class="upload-section">
      <FileUpload
        label="Добавить изображение"
        fileType="backstage_image"
        :onUpload="handleFileUpload"
        @file-uploaded="handleFileUploaded"
      />
    </div>
  </div>
</template>

<script>
import FileUpload from './FileUpload.vue';
import {
  getBackstageImages,
  createBackstageImage,
  deleteBackstageImage,
  updateBackstageImage
} from '@/services/api';

const API_URL = (process.env.VUE_APP_API_URL || 'http://localhost:8000').replace(/\/+$/, '');

export default {
  name: 'BackstageGalleryInline',
  components: { FileUpload },
  props: {
    title: {
      type: String,
      default: 'Backstage галерея'
    },
    context: {
      type: String,
      required: true
    },
    contextId: {
      type: [String, Number, null],
      default: null
    },
    allowReorder: {
      type: Boolean,
      default: false
    },
    showInfo: {
      type: Boolean,
      default: true
    },
    showActions: {
      type: Boolean,
      default: true
    },
    pendingItems: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:pendingItems'],
  data() {
    return {
      loading: false,
      error: null,
      images: [],
      draggedId: null
    };
  },
  computed: {
    displayImages() {
      return this.contextId ? this.images : this.pendingItems;
    },
    showMinimumHint() {
      const isUserOrProject = this.context === 'user' || this.context === 'project';
      return isUserOrProject && this.displayImages.length < 4;
    }
  },
  watch: {
    contextId: {
      handler() {
        this.loadImages();
      },
      immediate: true
    }
  },
  methods: {
    async loadImages() {
      if (!this.contextId) {
        this.images = [];
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        const data = await getBackstageImages({
          context: this.context,
          context_id: this.contextId
        });
        this.images = data?.images || [];
      } catch (err) {
        this.error = 'Ошибка загрузки изображений: ' + err.message;
      } finally {
        this.loading = false;
      }
    },
    async handleFileUpload() {
      // handled in FileUpload
    },
    async handleFileUploaded(data) {
      if (!data || !data.id) return;
      if (this.contextId) {
        try {
          const createdImage = await createBackstageImage({
            file_id: data.id,
            context: this.context,
            context_id: String(this.contextId),
            position: this.images.length > 0 ? Math.max(...this.images.map(i => i.position)) + 1 : 0
          });
          this.images.push(createdImage);
        } catch (err) {
          this.error = 'Ошибка добавления изображения: ' + err.message;
        }
      } else {
        const originalFilename = data.original_filename || data.file?.original_filename || data.name || 'file';
        const url = `${API_URL}/uploads/${data.id}-${originalFilename}`;
        const nextItems = [
          ...this.pendingItems,
          { id: data.id, file_id: data.id, url, original_filename: originalFilename }
        ];
        this.$emit('update:pendingItems', nextItems);
      }
    },
    async removeImage(image) {
      if (this.contextId) {
        await deleteBackstageImage(image.id);
        this.images = this.images.filter(item => item.id !== image.id);
        return;
      }
      const nextItems = this.pendingItems.filter(item => item.id !== image.id);
      this.$emit('update:pendingItems', nextItems);
    },
    handleDragStart(event, image) {
      this.draggedId = image.id;
      event.dataTransfer.effectAllowed = 'move';
    },
    handleDragOver(event) {
      event.dataTransfer.dropEffect = 'move';
    },
    async handleDrop(event, targetImage) {
      if (!this.draggedId || this.draggedId === targetImage.id) return;

      const list = [...this.displayImages];
      const draggedIndex = list.findIndex(item => item.id === this.draggedId);
      const targetIndex = list.findIndex(item => item.id === targetImage.id);
      if (draggedIndex === -1 || targetIndex === -1) return;

      const [moved] = list.splice(draggedIndex, 1);
      list.splice(targetIndex, 0, moved);

      if (this.contextId) {
        this.images = list.map((item, index) => ({ ...item, position: index }));
        for (let index = 0; index < this.images.length; index += 1) {
          const item = this.images[index];
          await updateBackstageImage(item.id, { position: item.position });
        }
      } else {
        const nextItems = list.map((item, index) => ({ ...item, position: index }));
        this.$emit('update:pendingItems', nextItems);
      }
    },
    handleDragEnd() {
      this.draggedId = null;
    }
  }
};
</script>

<style scoped>
.backstage-inline {
  margin-top: 16px;
}

.inline-title {
  margin: 0 0 8px;
  font-size: 1rem;
  font-weight: 600;
  color: hsl(var(--foreground));
}

.minimum-hint {
  margin-bottom: 8px;
  border: 1px dashed hsl(var(--warning) / 0.45);
  background: hsl(var(--warning) / 0.14);
  color: hsl(var(--foreground));
  border-radius: 10px;
  padding: 9px 11px;
  font-size: 0.82rem;
  line-height: 1.35;
}

.state-block {
  border: 1px solid hsl(var(--border));
  border-radius: 10px;
  background: hsl(var(--muted));
  color: hsl(var(--muted-foreground));
  padding: 16px;
  text-align: center;
  font-size: 0.9rem;
}

.state-block.error {
  border-color: hsl(var(--destructive) / 0.35);
  background: hsl(var(--destructive) / 0.1);
  color: hsl(var(--destructive));
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 12px;
  margin: 12px 0;
}

.image-card {
  background: hsl(var(--card));
  border: 1px solid hsl(var(--border));
  border-radius: 10px;
  padding: 10px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.image-card:hover {
  border-color: hsl(var(--accent) / 0.35);
  box-shadow: 0 8px 16px hsl(var(--foreground) / 0.08);
}

.image-card.is-draggable {
  cursor: grab;
}

.image-card.is-draggable:active {
  cursor: grabbing;
}

.image-preview img {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid hsl(var(--border));
}

.image-placeholder {
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: hsl(var(--muted));
  border-radius: 8px;
  color: hsl(var(--muted-foreground));
}

.image-info {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.image-meta-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  font-size: 0.72rem;
  color: hsl(var(--muted-foreground));
}

.image-meta-row code {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.7rem;
  color: hsl(var(--foreground));
  background: hsl(var(--muted));
  border: 1px solid hsl(var(--border));
  border-radius: 6px;
  padding: 2px 6px;
}

.image-actions {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
}

.upload-section {
  margin-top: 12px;
}
</style>
